import { useSyncExternalStore, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { subscribeOta, getOtaState, applyPendingOtaUpdate } from '@/lib/ota-update-manager';
import { useAppUpdate } from '@/hooks/useAppUpdate';

// A slim, dismissible banner that appears once a new OTA bundle has finished
// downloading and is staged. Tapping it activates the bundle deterministically
// (see applyPendingOtaUpdate) rather than waiting for the background→foreground
// auto-swap that some installs never complete. Native-only in practice — on web
// the OTA state never becomes `ready`, so this renders nothing.
//
// COPY IS DELIBERATELY A "REFRESH", NOT AN "UPDATE": this only swaps the
// in-app JS bundle and reloads the WebView (instant, stays in the app). It is a
// DIFFERENT thing from the App Store native-version prompt (UpdateAvailableBanner,
// which says "Open App Store" and leaves the app). Both used to say "Update",
// which read as one confusing thing to users — this one is the frequent, minor,
// stay-in-app refresh, so it's worded that way.
export function OtaUpdateBanner() {
  const state = useSyncExternalStore(subscribeOta, getOtaState, getOtaState);
  const [dismissed, setDismissed] = useState(false);
  const [applying, setApplying] = useState(false);
  // When the App Store has a newer NATIVE build, that update takes precedence:
  // it's the more important action (it leaves the app to install a new binary),
  // and resetWhenUpdate wipes any staged OTA bundle on the store install anyway,
  // so applying OTA first is pointless. Both banners are bottom-pinned in
  // separate render trees (this one at the root, UpdateAvailableBanner inside the
  // providers), and this one sits at a higher z-index — so without this guard it
  // would render ON TOP of the "Open App Store" banner and the user would tap the
  // refresh instead of the real update (Brian, 2026-08-06). Standing down here
  // leaves the App Store banner as the only one showing. useAppUpdate is
  // native-only + resolves async, so this is false until the store check returns
  // (offline / no update → stays false → the refresh banner shows normally).
  const { updateAvailable: nativeUpdatePending } = useAppUpdate();

  if (!state.ready || dismissed || nativeUpdatePending) return null;

  return (
    <div
      className="fixed inset-x-0 z-[10050] flex justify-center px-4"
      style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      role="status"
      aria-live="polite"
    >
      <div className="flex w-full max-w-md items-center gap-3 rounded-xl border border-purple-500/30 bg-slate-900/95 px-4 py-3 shadow-2xl backdrop-blur">
        <RefreshCw className={`h-5 w-5 flex-shrink-0 text-purple-400 ${applying ? 'animate-spin' : ''}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-100">Refresh to update</p>
          <p className="truncate text-xs text-slate-400">
            {applying ? 'Refreshing…' : 'A new version is ready — tap to reload the app.'}
          </p>
        </div>
        <button
          type="button"
          disabled={applying}
          onClick={() => {
            setApplying(true);
            // set() reloads the WebView, so this promise usually never resolves;
            // the spin state is just for the brief moment before the swap.
            applyPendingOtaUpdate().finally(() => setApplying(false));
          }}
          className="flex-shrink-0 rounded-lg bg-gradient-to-r from-purple-500 to-purple-700 px-4 py-2 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
        >
          Refresh
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
